import { Loader2, Workflow } from 'lucide-react'
import type { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { CaseSchemaStatus } from '@/features/schema/hooks/useCaseSchemaStatus'

interface SchemaSetupNoticeProps {
  caseId: string
  status: Exclude<CaseSchemaStatus, 'ready'>
  onRetry: () => void
}

interface SchemaSetupLinkProps {
  caseId: string
  /** Button height; the compact banner passes `sm`, dialog footers keep the default. */
  size?: ComponentProps<typeof Button>['size']
}

export function SchemaSetupLink({ caseId, size }: SchemaSetupLinkProps) {
  const { t } = useTranslation()
  return (
    <Button asChild size={size} className="shrink-0">
      <Link to={`/cases/${caseId}/schema`}>
        <Workflow className="size-3.5" aria-hidden />
        {t('cases.documents.schemaSetup.action')}
      </Link>
    </Button>
  )
}

export function SchemaSetupPrompt({ caseId, status, onRetry }: SchemaSetupNoticeProps) {
  const { t } = useTranslation()
  return (
    <>
      {/* Phone: the text keeps the first line (beside the banner icon) and the
          action wraps under it, instead of the text being squeezed into a sliver. */}
      <div className="min-w-0 flex-1 max-md:basis-[calc(100%-2rem)]" role="status">
        <p className="text-sm font-medium text-foreground">
          {t(`cases.documents.schemaSetup.${status}.title`)}
        </p>
        <p className="text-xs text-muted-foreground">
          {t(`cases.documents.schemaSetup.${status}.description`)}
        </p>
      </div>
      {status === 'required' && <SchemaSetupLink caseId={caseId} size="sm" />}
      {status === 'error' && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t('cases.documents.schemaSetup.retry')}
        </Button>
      )}
    </>
  )
}

export function SchemaSetupBanner(props: SchemaSetupNoticeProps) {
  const Icon = props.status === 'loading' ? Loader2 : Workflow
  return (
    <Card className="mx-4 mt-3 flex shrink-0 md:mx-6 flex-row flex-wrap items-center gap-3 border-primary/30 bg-primary/5 px-4 py-3 max-md:[&>a]:flex-1 max-md:[&>button]:flex-1">
      <Icon
        className={`size-4 shrink-0 text-primary ${props.status === 'loading' ? 'animate-spin' : ''}`}
        aria-hidden
      />
      <SchemaSetupPrompt {...props} />
    </Card>
  )
}
